import { useState, useCallback } from 'react';

interface UseCopyToClipboardReturn {
  /** Whether the text was recently copied (for showing confirmation) */
  copied: boolean;
  /** Copy text to clipboard */
  copy: (text: string) => Promise<void>;
  /** Error message if copy failed */
  error: string | null;
}

/**
 * Hook for copying text to the clipboard with confirmation state.
 * The `copied` state resets after 2 seconds.
 */
export function useCopyToClipboard(): UseCopyToClipboardReturn {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setError(null);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
      setCopied(false);
    }
  }, []);

  return { copied, copy, error };
}
